import { useState } from 'react';
import classNames from 'classnames';
import { Button } from 'ui/components';
import { ReactComponent as IconChevronDown } from 'ui/icons/icon-chevron-down-big.svg';
import styles from './DropDown.module.scss';

export interface DropdownProps {
    items: string[];
    selectedIndex?: number;
    onSelect: (index: number, item: string) => void;
    disabled?: boolean;
    className?: string;
    placeholder?: string;
}

export const Dropdown: React.FC<DropdownProps> = ({
    items,
    selectedIndex = 0,
    onSelect,
    disabled = false,
    className,
    placeholder = "Select an option"
}) => {
    const [isOpen, setIsOpen] = useState(false);

    const handleItemSelect = (index: number) => {
        onSelect(index, items[index]);
        setIsOpen(false);
    };

    const toggleDropdown = () => {
        setIsOpen(!isOpen);
    };

    if (!items || items.length === 0) {
        return null;
    }

    const selectedItem = items[selectedIndex];

    return (
        <div className={classNames(className)}>
            <Button
                className={styles.dropdownTitle} 
                onClick={toggleDropdown} 
                disabled={disabled}
                aria-expanded={isOpen}
                aria-haspopup="listbox"
                aria-controls="dropdown-menu"
                id="dropdown-button" 
            >
                <div className={styles.buttonContent}>
                    <span className={styles.buttonText}>
                        {selectedItem || placeholder}
                    </span>
                    <IconChevronDown 
                        aria-hidden="true" 
                        className={classNames(styles.chevron, {
                            [styles.chevronUp]: isOpen
                        })} 
                    />
                </div>
            </Button>
            {isOpen && (
                <ul className={styles.dropdownMenu}
                    role="listbox"
                    id="dropdown-menu"
                    aria-labelledby="dropdown-button">
                    {items.map((item, index) => (
                        <li
                            role="option"
                            aria-selected={index === selectedIndex}
                            key={index}
                            className={classNames(styles.dropdownItem, {
                                [styles.active]: index === selectedIndex
                            })}
                            onClick={() => handleItemSelect(index)}
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleItemSelect(index);
                                }
                                if (e.key === 'Escape'){
                                    e.preventDefault();
                                    setIsOpen(false);
                                }
                            }}
                        >
                            {item}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};